import { useCallback } from "react";
import { useSetAtom } from "jotai";
import { useCustomer } from "autumn-js/react";
import { proDialog, topUpDialog } from "@/state/dialogs";
import { isAdvancedModel } from "@/lib/aiproviders/models";
import { useUser } from "@/hooks/useUser";
import { FEATURE_IDS } from "@/config/products";

export const useAIUsageCheck = () => {
  const { hasPro } = useUser();
  const { check } = useCustomer();
  const setProDialogOpen = useSetAtom(proDialog);
  const setTopUpDialogOpen = useSetAtom(topUpDialog);

  const checkUsage = useCallback(
    (modelId: string): boolean => {
      const isAdvanced = isAdvancedModel(modelId);
      const requiredBalance = isAdvanced ? 4 : 1;

      // Check available credits
      const primaryCredits = hasPro
        ? (check({ featureId: FEATURE_IDS.CREDIT_PRO, requiredBalance })?.data?.balance ?? 0)
        : (check({ featureId: FEATURE_IDS.CREDIT_FREE, requiredBalance })?.data?.balance ?? 0);
      const topUpCredits =
        check({ featureId: FEATURE_IDS.CREDIT_TOP_UP, requiredBalance })?.data?.balance ?? 0;
      const hasAnyCredits = primaryCredits >= requiredBalance || topUpCredits >= requiredBalance;

      // Free users can only use advanced models if they have top-up credits
      if (!hasPro && isAdvanced && topUpCredits < requiredBalance) {
        setProDialogOpen(true);
        return false;
      }

      // Check if user has any available credits
      if (!hasAnyCredits) {
        if (hasPro) {
          setTopUpDialogOpen(true);
        } else {
          setProDialogOpen(true);
        }
        return false;
      }

      return true;
    },
    [hasPro, check, setProDialogOpen, setTopUpDialogOpen],
  );

  return {
    checkUsage,
  };
};
